/**
 * Standalone Broadcasts API Client
 *
 * Professional API client for standalone broadcast operations.
 * All broadcasts are created and managed without explicit campaign context.
 *
 * @module api/broadcasts
 */

import AspApi from '../../../api/ApiConfig';

/**
 * Fetch all broadcasts for a brand (standalone + legacy)
 *
 * @param {number} brandId - Brand ID
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.search - Search query
 * @param {string} options.status - Filter by status
 * @param {string} options.sortBy - Sort field (created_at, name, status)
 * @param {string} options.sortOrder - Sort order (asc, desc)
 * @returns {Promise<Object>} Broadcasts list with pagination
 */
export const fetchBroadcastsList = async (brandId, options = {}) => {
  const {
    page = 1,
    limit = 20,
    search = '',
    status = '',
    sortBy = 'created_at',
    sortOrder = 'desc',
    campaignId = null
  } = options;

  const params = new URLSearchParams({
    brand_id: brandId,
    page,
    limit,
    ...(campaignId && { campaign_id: campaignId }),
    ...(search && { search }),
    ...(status && { status }),
    sort_by: sortBy,
    sort_order: sortOrder
  });

  const res = await AspApi.get(
    `/api/v1/svc/campaign_broadcast/broadcasts?${params.toString()}`
  );
  return res;
};

/**
 * Create a new standalone broadcast
 *
 * @param {number} brandId - Brand ID
 * @param {Object} data - Broadcast creation data
 * @param {string} data.name - Broadcast name
 * @param {string} data.start_date - Start date (ISO format)
 * @param {string} data.end_date - End date (ISO format)
 * @param {Object} data.settings - Broadcast settings
 * @param {Object} options - Additional options
 * @param {string} options.campaignId - Optional campaign ID for legacy mode
 * @param {string} options.broadcastType - Broadcast type ('sales' | 'custom')
 *                                         'sales' creates campaign with Sales goal
 *                                         'custom' creates campaign with default Awareness goal
 * @returns {Promise<Object>} Created broadcast data
 */
export const createStandaloneBroadcast = async (brandId, data, options = {}) => {
  const { campaignId = null, broadcastType = null } = options;

  const params = new URLSearchParams({
    brand_id: brandId,
    ...(campaignId && { campaign_id: campaignId }),
    ...(broadcastType && { broadcast_type: broadcastType }),
  });

  const res = await AspApi.post(
    `/api/v1/svc/campaign_broadcast/broadcasts?${params.toString()}`,
    data
  );
  return res;
};

/**
 * Get broadcast details by ID
 *
 * @param {number} broadcastId - Broadcast ID
 * @returns {Promise<Object>} Broadcast details
 */
export const getBroadcastDetails = async (broadcastId, brandId, campaignId = null) => {
  const params = new URLSearchParams({
    brand_id: brandId,
    ...(campaignId && { campaign_id: campaignId })
  });

  const res = await AspApi.get(
    `/api/v1/svc/campaign_broadcast/broadcasts/${broadcastId}?${params.toString()}`
  );
  return res;
};

/**
 * Update broadcast
 *
 * @param {number} broadcastId - Broadcast ID
 * @param {Object} data - Update data
 * @returns {Promise<Object>} Updated broadcast
 */
export const updateBroadcast = async (broadcastId, brandId, data, campaignId = null) => {
  const params = new URLSearchParams({
    brand_id: brandId,
    ...(campaignId && { campaign_id: campaignId })
  });

  const res = await AspApi.put(
    `/api/v1/svc/campaign_broadcast/broadcasts/${broadcastId}?${params.toString()}`,
    data
  );
  return res;
};

/**
 * Delete broadcast
 *
 * @param {number} broadcastId - Broadcast ID
 * @param {number} brandId - Brand ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteBroadcast = async (broadcastId, brandId, campaignId = null) => {
  const params = new URLSearchParams({
    brand_id: brandId,
    ...(campaignId && { campaign_id: campaignId })
  });

  const res = await AspApi.delete(
    `/api/v1/svc/campaign_broadcast/broadcasts/${broadcastId}?${params.toString()}`
  );
  return res;
};

/**
 * Retry failed broadcast
 *
 * @param {number} broadcastId - Broadcast ID
 * @param {number} brandId - Brand ID
 * @returns {Promise<Object>} Retry response
 */
export const retryBroadcast = async (broadcastId, brandId) => {
  const res = await AspApi.put(
    `/api/v1/svc/campaign_broadcast/broadcasts/${broadcastId}/reactivate?brand_id=${brandId}`,
  );
  return res;
};

/**
 * Get broadcast metrics/analytics
 *
 * @param {number} broadcastId - Broadcast ID
 * @param {string} startDate - Start date for metrics
 * @param {string} endDate - End date for metrics
 * @returns {Promise<Object>} Broadcast metrics
 */
export const getBroadcastMetrics = async (broadcastId, startDate, endDate) => {
  const res = await AspApi.get(
    `/api/v1/svc/campaign_broadcast/broadcasts/${broadcastId}/metrics?start_date=${startDate}&end_date=${endDate}`
  );
  return res;
};
/**
 * Fetch broadcast contact list (for report download)
 *
 * @param {number} brandId - Brand ID
 * @param {number} broadcastId - Broadcast ID
 * @param {Object} query - Query parameters (status, search, download_all)
 * @returns {Promise<Object>} Contact list
 */
export const fetchBroadcastContactList = async (brandId, broadcastId, query = {}) => {
  const params = new URLSearchParams({
    brand_id: brandId,
    broadcast_id: broadcastId,
    ...query,
  });

  const res = await AspApi.get(
    `/api/v1/svc/whatsapp/conversations/broadcast_conversation_list?${params.toString()}`
  );
  return res;
};

/**
 * Poll export report status (for async large-file downloads)
 *
 * @param {number} brandId - Brand ID
 * @param {string} reportType - Report type (e.g. 'broadcast_contacts')
 * @returns {Promise<Object>} Export report status
 */
export const exportReportStatus = async (brandId, reportType) => {
  const params = new URLSearchParams({
    brand_id: brandId,
    report_type: reportType,
  });

  const res = await AspApi.get(
    `/api/v1/svc/whatsapp/conversations/export_report_status?${params.toString()}`
  );
  return res;
};

/**
 * Mark export report as seen by user (stops polling)
 *
 * @param {number} brandId - Brand ID
 * @param {Object} payload - { export_report_id }
 * @returns {Promise<Object>} Response
 */
export const exportReportUserSeen = async (brandId, payload) => {
  const params = new URLSearchParams({ brand_id: brandId });

  const res = await AspApi.put(
    `/api/v1/svc/whatsapp/conversations/export_report_user_seen?${params.toString()}`,
    payload
  );
  return res;
};
